import React, { useContext, useEffect } from 'react';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { isNotNullOrUndefined } from '@utils/utils';
import {
  shortDateFormat,
  stringIsoFromDateInTz,
  dayjsFromApiPrague,
  dayjsFromLocalDateString, stringYMDFromDate,
} from '@utils/dateWithTz';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { DocumentTypeEnum } from '@api/ringil-api.v2';
import useForm, { proxiedPropertiesOf } from '@hooks/useForm/useForm';
import { ButtonRow } from '@components/styles';
import HorizontalFieldValue from '@components/obsolete/HorizontalFieldValue.tsx';
import { InputDocumentType } from '@components/obsolete/Form/InputDocumentType/InputDocumentType.tsx';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';

interface ComponentProps {
  documentId: string;
  onHide: () => void;
}

interface Form {
  userDate: string;
  userNote: string;
  documentType: DocumentTypeEnum;
}

const EditDocumentMetadata: React.FC<ComponentProps> = ({ documentId, onHide }) => {
  const { tr } = useTranslationLgs();
  const { getDocumentMetadata, updateDocumentMetadata } = useContext(ApiContext);
  const { loggedUser } = useContext(AuthenticatedUserContext);
  const { i18n } = useTranslation();

  const names = proxiedPropertiesOf<Form>();
  const form = useForm<Form>(yup.object().shape({}), { userNote: '', userDate: null, documentType: null }, d => save(d), false, false);

  useEffect(() => {
    if (documentId) {
      getDocumentMetadata(documentId, d => {
        form.setValues({
          userDate: d.userDateLocalDate,
          userNote: d.userNote,
          documentType: d.documentType,
        } as Form);
      });
    }
  }, [documentId]);

  const save = (data: Form) => {
    updateDocumentMetadata(
      documentId,
      {
        userNote: data.userNote,
        userDateLocalDate: data.userDate,
        documentType: data.documentType,
      },
      () => {
        onHide();
      },
    );
  };

  return (
    <>
      {/*
        // @ts-ignore*/}
      <Dialog visible={isNotNullOrUndefined(documentId)} onHide={onHide} style={{ width: '450px' }} header={tr('EditDocumentMetadata.title', 'Copying a document')}>
        {form.calledSetValues && (
          <>
            <HorizontalFieldValue
              label={tr('EditDocumentMetadata.documentType', 'Document type')}
              value={<InputDocumentType documentType={form.values.documentType} onChange={v => form.setFieldValue(names.documentType, v.documentType)} />}
            />
            <HorizontalFieldValue
              label={tr('EditDocumentMetadata.date', 'Date')}
              value={
                <Calendar
                  showIcon={true}
                  value={form.values.userDate ? dayjsFromLocalDateString(form.values.userDate).toDate() : null}
                  onChange={e => form.setFieldValue(names.userDate, stringYMDFromDate(e.value as Date))}
                  dateFormat={shortDateFormat}
                  selectionMode={'single'}
                  style={{ width: '100%' }}
                  locale={i18n.language}
                  hideOnDateTimeSelect={true}
                />
              }
            />
            <HorizontalFieldValue
              label={tr('EditDocumentMetadata.note', 'Note')}
              value={
                <InputTextarea
                  style={{ width: '100%', height: '100%' }}
                  value={form.values.userNote}
                  onChange={e => form.setFieldValue(names.userNote, e.target.value)}
                />
              }
            />
            <ButtonRow>
              <Button label={tr(`EditDocumentMetadata.save`, `Save`)} onClick={e => form.validateAndSend()} />
              <Button label={tr(`EditDocumentMetadata.cancel`, `Cancel`)} className="p-button-text" onClick={() => onHide()} />
            </ButtonRow>
          </>
        )}
      </Dialog>
    </>
  );
};

export default EditDocumentMetadata;
